import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// import { useFocusEffect } from '@react-navigation/native';
// import { showMessage } from 'react-native-flash-message';
// import { Alert } from 'react-native';

import {
  useCommentsQuery,
  usePinnedCommentsQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useReactToCommentMutation,
  useReportCommentMutation,
  useTogglePinCommentMutation,
  useFlagCommentMutation,
  useInvalidateComments,
} from '@cobu/api-client';
import { COMMENTS_KEYS } from '@cobu/api-client';
import { commentsService } from '@cobu/api-client';
import {
  GroupComment,
  CommentInput,
  CommentType,
  UseGroupDiscussionReturn,
  CommentsResponse
} from '@cobu/types';
import { Strings } from '../constants';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { isMarketplaceContent, findMarketplaceGroupId } from '../constants';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { logger } from '../utils/logger';


interface UseGroupDiscussionConfig {
  /** Group ID for group-specific discussions */
  groupId?: string;
  /** Event ID for event-specific discussions */
  groupEventId?: string;
  /** Building ID for public building chat */
  buildingId?: string;
  /** Event date for group event comments (YYYY-MM-DD format) */
  eventDate?: string;
  /** Type of comments to manage */
  commentType: CommentType;
  /** Whether the user is allowed to view/fetch this discussion (default: true) */
  enabled?: boolean;

  // Feature flags
  /** Allow users to react to comments with emojis */
  allowReactions?: boolean;
  /** Allow users to reply to comments */
  allowReplies?: boolean;
  /** Allow users to edit their own comments */
  allowEditing?: boolean;
  /** Allow moderators to pin important comments */
  allowPinning?: boolean;
  /** Allow users to report inappropriate content */
  allowReporting?: boolean;
  /** Allow users to upload images with comments */
  allowImageUploads?: boolean;

  // Configuration
  /** Maximum allowed characters in a comment (default: 2000) */
  maxCommentLength?: number;
  /** Maximum number of images per comment (default: 5) */
  maxImages?: number;
  /** Enable automatic refresh of comments (default: false) */
  enableAutoRefresh?: boolean;
  /** Refresh interval in milliseconds (default: 30000) */
  refreshInterval?: number;

  // Callbacks
  /** Called when user taps on a profile */
  onNavigateToProfile?: (userId: string) => void;
  /** Called when user wants to compose a new comment or reply */
  onNavigateToCompose?: (replyTo?: GroupComment) => void;
  /** Called when user wants to see all group members */
  onShowAllMembers?: () => void;
}

// interface UseGroupDiscussionReturn {
//   // Data
//   comments: GroupComment[];
//   pinnedComments: GroupComment[];
//   commentCount: number;
//   
//   // State
//   loading: boolean;
//   refreshing: boolean;
//   replyLoading: string | null;
//   hiddenPinnedComments: Record<string, boolean>;
//   
//   // Actions
//   createComment: (input: CommentInput) => Promise<void>;
//   updateComment: (commentId: string, updates: Partial<CommentInput>) => Promise<void>;
//   deleteComment: (comment: GroupComment) => Promise<void>;
//   reactToComment: (commentId: string, reaction: string | null) => Promise<void>;
//   reportUser: (userId: string, reason: string) => Promise<void>;
//   
//   // UI Actions
//   hidePinnedComment: (commentId: string) => void;
//   refreshComments: () => Promise<void>;
//   loadMoreComments: () => Promise<void>;
//   
//   // Pagination
//   hasMoreComments: boolean;
//   isLoadingMore: boolean;
//   togglePinComment: (commentId: string, isPinned: boolean) => Promise<void>;
//   flagComment: (commentId: string) => Promise<void>;
//   
//   // Error handling
//   error: string | null;
//   clearError: () => void;
// }

export const useGroupDiscussion = (config: UseGroupDiscussionConfig): UseGroupDiscussionReturn => {
  const {
    groupId,
    groupEventId,
    buildingId,
    eventDate,
    commentType,
    enabled = true,
    allowReactions = true,
    allowReplies = true,
    allowEditing = true,
    allowPinning = false,
    allowReporting = true,
    allowImageUploads = true,
    maxCommentLength = 2000,
    maxImages = 5,
    enableAutoRefresh = false,
    refreshInterval = 30000, // 30 seconds
    onNavigateToProfile,
    onNavigateToCompose,
    onShowAllMembers,
  } = config;

  // Get strings and user from store
  const strings = Strings.get().discussion;
  const { user } = useSelector((state: RootState) => state.auth);


  // Local state for UI interactions
  const [hiddenPinnedComments, setHiddenPinnedComments] = useState<Record<string, boolean>>({});
  const [replyLoading, setReplyLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Data queries
  const commentsQuery = useCommentsQuery(groupId, groupEventId, buildingId, eventDate, enabled);
  const pinnedCommentsQuery = usePinnedCommentsQuery(buildingId, groupId, groupEventId, enabled);

  // Refs so the auto-refresh effect can read the latest query state without
  // depending on the query objects themselves (new references every render).
  const commentsQueryRef = useRef(commentsQuery);
  commentsQueryRef.current = commentsQuery;
  const pinnedCommentsQueryRef = useRef(pinnedCommentsQuery);
  pinnedCommentsQueryRef.current = pinnedCommentsQuery;

  // Mutation hooks
  const createCommentMutation = useCreateCommentMutation(commentType, groupId, groupEventId, buildingId, eventDate);
  const updateCommentMutation = useUpdateCommentMutation(commentType, groupId, groupEventId, buildingId);
  const deleteCommentMutation = useDeleteCommentMutation(commentType, groupId, groupEventId, buildingId);
  const reactToCommentMutation = useReactToCommentMutation(commentType, groupId, groupEventId, buildingId, eventDate, user?.uuid);
  const reportCommentMutation = useReportCommentMutation();
  const togglePinCommentMutation = useTogglePinCommentMutation(commentType, groupId, buildingId);
  const flagCommentMutation = useFlagCommentMutation(commentType);
  const invalidateComments = useInvalidateComments();

  // Query client for targeted refetches
  const queryClient = useQueryClient();

  // Resolve the comments query key used by the infinite query
  const commentsQueryKey = useMemo(() => (
    eventDate
      ? COMMENTS_KEYS.listWithDate(groupId, groupEventId, buildingId, eventDate)
      : COMMENTS_KEYS.list(groupId, groupEventId, buildingId)
  ), [groupId, groupEventId, buildingId, eventDate]);

  // Auto-refresh when enabled
  useEffect(() => {
    if (!enableAutoRefresh || !enabled) return;

    let timeoutId: any;
    let intervalId: any;

    // Refetch comments when auto-refresh is enabled.
    // Routine ticks only refetch page 1 — that's the only page that changes
    // tick-to-tick. Page 2 and the pinned comments are reserved for `full`
    // fetches (initial start, and returning to a tab that was hidden).
    const refetchAll = async ({ full = false }: { full?: boolean } = {}) => {
      const commentsQuery = commentsQueryRef.current;
      if (commentsQuery.isPending || commentsQuery.isRefetching || commentsQuery.isFetching) return;
      try {
        const fetchPage = (pageNum: number) => {
          if (buildingId && !groupId && !groupEventId) {
            return commentsService.getBuildingChatComments(buildingId, pageNum);
          }
          if (groupEventId && !groupId && !buildingId) {
            return commentsService.getEventChatComments(groupEventId, pageNum, 20, eventDate);
          }
          return commentsService.getComments(groupId, groupEventId, pageNum, 20, eventDate);
        };

        const hasSecondPageLoaded = Array.isArray(commentsQuery.data?.pages) && commentsQuery.data!.pages.length > 1;
        const requests: Promise<CommentsResponse>[] = [fetchPage(1)];
        if (full && hasSecondPageLoaded) {
          requests.push(fetchPage(2));
        }
        const [firstPage, secondPage] = await Promise.all(requests);

        queryClient.setQueryData(
          commentsQueryKey,
          (oldData: any) => {
            if (!oldData) return oldData;

            const oldPages: CommentsResponse[] = Array.isArray(oldData.pages) ? [...oldData.pages] : [];

            // If no existing pages, just set the first two pages as fetched
            if (oldPages.length === 0) {
              const pages: CommentsResponse[] = [];
              if (firstPage) pages.push(firstPage);
              if (secondPage) pages.push(secondPage);
              return { ...oldData, pages };
            }

            // Collect existing IDs across all pages to compare
            const existingIds = new Set<string>();
            oldPages.forEach((p) => {
              (p?.comments || []).forEach((c: GroupComment) => existingIds.add(c.uuid));
            });

            // Build list of new items from page 1 then page 2, preserving order and de-duping
            const newItems: GroupComment[] = [];
            const seenNew = new Set<string>();
            const addUnique = (list?: GroupComment[]) => {
              (list || []).forEach((c) => {
                if (!existingIds.has(c.uuid) && !seenNew.has(c.uuid)) {
                  seenNew.add(c.uuid);
                  newItems.push(c);
                }
              });
            };

            addUnique(firstPage?.comments);
            addUnique(secondPage?.comments);

            // If nothing new, keep cache as-is
            if (newItems.length === 0) {
              return oldData;
            }

            // Remove any of the new items from all existing pages to avoid duplicates
            const filteredPages = oldPages.map((p) => ({
              ...p,
              comments: (p?.comments || []).filter((c) => !seenNew.has(c.uuid))
            }));

            // Prepend new items to the first page's comments and update metadata from firstPage
            const firstExisting = filteredPages[0] || ({ comments: [], hasMore: false, page: 1, total: 0 } as CommentsResponse);
            const updatedFirst: CommentsResponse = {
              ...firstExisting,
              comments: [...newItems, ...(firstExisting.comments || [])],
              hasMore: firstPage?.hasMore ?? firstExisting.hasMore,
              total: firstPage?.total ?? firstExisting.total,
              page: 1
            };

            const newPages = [updatedFirst, ...filteredPages.slice(1)];
            return { ...oldData, pages: newPages };
          }
        );
      } catch (err) {
        logger.error('Failed to refetch first two pages:', err);
      }
      if (full && !groupId && !groupEventId && buildingId) {
        pinnedCommentsQueryRef.current.refetch();
      }
    };

    const stopPolling = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const startPolling = () => {
      if (timeoutId || intervalId) return;

      // Align the first refresh to avoid back-to-back calls with the periodic
      // timer. Data older than one interval (which is the case whenever the tab
      // was hidden past a tick) gets a full catch-up fetch right away.
      const age = Math.max(0, Date.now() - (commentsQueryRef.current.dataUpdatedAt || 0));

      if (age > refreshInterval) {
        // Too old: catch up now, then start the periodic timer immediately
        refetchAll({ full: true });
        intervalId = setInterval(() => refetchAll(), refreshInterval);
      } else {
        // Fresh enough: schedule first tick for the remaining time, then continue periodically
        timeoutId = setTimeout(() => {
          timeoutId = null;
          refetchAll();
          intervalId = setInterval(() => refetchAll(), refreshInterval);
        }, refreshInterval - age);
      }
    };

    // Only poll while the tab is actually visible — background tabs left open
    // otherwise keep hitting the API for no one to see.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enableAutoRefresh, enabled, refreshInterval, groupId, groupEventId, buildingId, commentsQueryKey, queryClient, eventDate]);

  // Memoized computed values
  const comments = useMemo(() => {
    if (!commentsQuery.data) return [];

    return commentsQuery.data.pages
      .flatMap(page => page.comments)
      .filter(comment =>
        !comment.removed ||
        comment.removedBy === 'USER' ||
        comment.children.length > 0
      );
  }, [commentsQuery.data]);

  const pinnedComments = useMemo(() => {
    return pinnedCommentsQuery.data?.filter(comment =>
      !comment.hidden && !hiddenPinnedComments[comment.uuid]
    ) || [];
  }, [pinnedCommentsQuery.data, hiddenPinnedComments]);

  const commentCount = useMemo(() => {
    return comments.reduce((count, comment) => {
      return count + 1 + (comment.children?.length || 0);
    }, 0);
  }, [comments]);

  const hasMoreComments = useMemo(() => {
    const lastPage = commentsQuery.data?.pages[commentsQuery.data.pages.length - 1];
    return lastPage?.hasMore || false;
  }, [commentsQuery.data]);

  // Error handling
  const handleError = useCallback((error: any, defaultMessage: string) => {
    logger.error('GroupDiscussion error:', error);
    const message = error?.message || defaultMessage;
    setError(message);
    // showMessage({
    //   message,
    //   type: 'danger',
    //   position: 'top',
    // });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validation functions
  const validateCommentInput = useCallback((input: CommentInput): string | null => {
    if (!input.contents.trim()) {
      return strings.validation.commentEmpty;
    }

    if (input.contents.length > maxCommentLength) {
      return strings.errors.commentTooLong;
    }

    if (allowImageUploads && input.images && input.images.length > maxImages) {
      return strings.errors.tooManyImages;
    }

    return null;
  }, [maxCommentLength, maxImages, allowImageUploads, strings]);

  // Helper function to show marketplace alert
  const showMarketplaceAlert = useCallback((input: CommentInput, groups: any[], submissionHandler: (input: CommentInput) => Promise<void>) => {
    const marketplaceGroupId = findMarketplaceGroupId(groups);
    // Alert.alert(
    //   strings.marketplace.detectedTitle,
    //   strings.marketplace.detectedMessage,
    //   [
    //     {
    //       text: strings.marketplace.confirmYes,
    //       style: 'default',
    //       onPress: () => {
    //         const updatedInput = marketplaceGroupId
    //           ? { ...input, groupId: marketplaceGroupId }
    //           : input;
    //         submissionHandler(updatedInput);
    //       },
    //     },
    //     {
    //       text: strings.marketplace.confirmNo,
    //       style: 'cancel',
    //       onPress: () => submissionHandler(input),
    //     },
    //   ]
    // );
    // Fallback: proceed without UI confirmation
    submissionHandler(
      marketplaceGroupId ? { ...input, groupId: marketplaceGroupId } : input
    );
  }, [strings]);

  // Helper function to handle actual comment submission
  const handleCommentSubmission = useCallback(async (input: CommentInput) => {
    try {
      // Set reply loading state if this is a reply
      if (input.parent) {
        setReplyLoading(input.parent);
      }

      // Add context IDs to input
      const commentInput = {
        ...input,
        groupId: groupId || input.groupId,
        groupEventId: groupEventId || input.groupEventId,
      };

      await createCommentMutation.mutateAsync(commentInput);

      // Clear reply loading state
      setReplyLoading(null);

      // Show success message
      // showMessage({
      //   message: input.parent ? strings.success.replyCreated : strings.success.commentCreated,
      //   type: 'success',
      //   position: 'top',
      // });


    } catch (error: any) {
      setReplyLoading(null);
      handleError(error, strings.errors.createFailed);
    }
  }, [
    createCommentMutation,
    handleError,
    strings,
    groupId,
    groupEventId,
    commentType,
    buildingId,
    eventDate
  ]);

  // Action functions
  const createComment = useCallback(async (input: CommentInput, enableMarketplaceModerator?: boolean, groups?: any[]) => {
    try {
      clearError();

      // Validate input
      const validationError = validateCommentInput(input);
      if (validationError) {
        throw new Error(validationError);
      }

      // Check for marketplace content if enabled and not a reply
      if (
        enableMarketplaceModerator &&
        !input.parent &&
        groups &&
        isMarketplaceContent(input.contents)
      ) {
        showMarketplaceAlert(input, groups, handleCommentSubmission);
        return;
      }

      /* ANALYTICS_DISABLED: CREATE_GROUP_COMMENT ('create_group_comment')
        analyticsService.trackEvent(AnalyticsEvent.CREATE_GROUP_COMMENT, {
                input,
              });
        */

      // Proceed with normal comment submission
      await handleCommentSubmission(input);

    } catch (error: any) {
      setReplyLoading(null);
      handleError(error, strings.errors.createFailed);
    }
  }, [
    validateCommentInput,
    clearError,
    handleError,
    strings,
    showMarketplaceAlert,
    handleCommentSubmission
  ]);

  const updateComment = useCallback(async (commentId: string, updates: Partial<CommentInput>) => {
    try {
      clearError();

      // Validate updates if content is being changed
      if (updates.contents) {
        const validationError = validateCommentInput(updates as CommentInput);
        if (validationError) {
          throw new Error(validationError);
        }
      }

      await updateCommentMutation.mutateAsync({ commentId, updates });

      // showMessage({
      //   message: strings.success.commentUpdated,
      //   type: 'success',
      //   position: 'top',
      // });

    } catch (error: any) {
      handleError(error, strings.errors.updateFailed);
    }
  }, [updateCommentMutation, validateCommentInput, clearError, handleError, strings]);

  const deleteComment = useCallback(async (comment: GroupComment) => {
    try {
      clearError();

      await deleteCommentMutation.mutateAsync(comment.uuid);

      // showMessage({
      //   message: strings.success.commentDeleted,
      //   type: 'success',
      //   position: 'top',
      // });

      // Track analytics
      analyticsService.trackEvent(AnalyticsEvent.DELETE_COMMENT, {
        commentId: comment.uuid,
        commentType,
        groupId,
        groupEventId,
      });

    } catch (error: any) {
      handleError(error, strings.errors.deleteFailed);
    }
  }, [deleteCommentMutation, clearError, handleError, strings, commentType, groupId, groupEventId]);

  const reactToComment = useCallback(async (commentId: string, reaction: string | null) => {
    if (!allowReactions) return;

    try {
      clearError();

      // Find the comment to determine current reaction, check for pinned comments and replies as well
      const comment =
        comments.find(c => c.uuid === commentId) ||
        comments
          .find(c => c.children.some(child => child.uuid === commentId))
          ?.children.find(child => child.uuid === commentId) ||
        pinnedComments.find(c => c.uuid === commentId) ||
        pinnedComments
          .find(c => c.children.some(child => child.uuid === commentId))
          ?.children.find(child => child.uuid === commentId);

      if (!comment) {
        throw new Error(strings.validation.commentNotFound);
      }

      const isPinned =
        pinnedComments.find(c => c.uuid === commentId) ||
        pinnedComments
          .find(c => c.children.some(child => child.uuid === commentId))
          ?.children.find(child => child.uuid === commentId)
          ? true
          : false;

      // Treat null reaction (from remove flow) OR selecting the same reaction again as removal
      const isRemoving = reaction === null || comment.myReaction === reaction;
      // @ts-ignore
      const parentCommentId = comment.parent ? (typeof comment.parent === 'string' ? comment.parent : comment.parent?.uuid) : undefined;

      await reactToCommentMutation.mutateAsync({
        commentId,
        reactionName: isRemoving ? null : reaction,
        isRemoving,
        parentCommentId,
        isPinned: isPinned,
      });

    } catch (error: any) {
      handleError(error, strings.errors.reactionFailed);
    }
  }, [
    allowReactions,
    comments,
    clearError,
    handleError,
    strings
  ]);

  const reportUser = useCallback(async (userId: string, reason: string) => {
    if (!allowReporting) return;

    try {
      clearError();

      await reportCommentMutation.mutateAsync({
        commentId: userId, // This might need adjustment based on API
        reason,
      });

      // showMessage({
      //   message: strings.moderation.reportSubmitted,
      //   type: 'success',
      //   position: 'top',
      // });

    } catch (error: any) {
      handleError(error, strings.errors.reportFailed);
    }
  }, [allowReporting, reportCommentMutation, clearError, handleError, strings]);

  // UI action functions
  const hidePinnedComment = useCallback((commentId: string) => {
    setHiddenPinnedComments(prev => ({
      ...prev,
      [commentId]: true,
    }));
  }, []);

  const refreshComments = useCallback(async () => {
    if (!enabled) return;

    try {
      await Promise.all([
        commentsQuery.refetch(),
        (!groupId && !groupEventId && buildingId) ? pinnedCommentsQuery.refetch() : Promise.resolve(),
      ]);
    } catch (error: any) {
      handleError(error, strings.errors.loadFailed);
    }
  }, [enabled, commentsQuery, pinnedCommentsQuery, handleError, strings, groupId, groupEventId, buildingId]);

  const loadMoreComments = useCallback(async () => {
    if (enabled && hasMoreComments && !commentsQuery.isFetchingNextPage) {
      try {
        await commentsQuery.fetchNextPage();
      } catch (error: any) {
        handleError(error, strings.errors.loadFailed);
      }
    }
  }, [enabled, hasMoreComments, commentsQuery, handleError, strings]);

  // Additional utility functions for specific comment types
  const togglePinComment = useCallback(async (commentId: string, isPinned: boolean) => {
    if (!allowPinning) return;

    try {
      clearError();
      await togglePinCommentMutation.mutateAsync({ commentId, isPinned });

      // showMessage({
      //   message: isPinned ? strings.success.commentPinned : strings.success.commentUnpinned,
      //   type: 'success',
      //   position: 'top',
      // });

    } catch (error: any) {
      handleError(error, strings.errors.togglePinFailed);
    }
  }, [allowPinning, togglePinCommentMutation, clearError, handleError, strings]);

  const flagComment = useCallback(async (commentId: string) => {
    try {
      clearError();
      await flagCommentMutation.mutateAsync({ commentId });
    } catch (error: any) {
      handleError(error, strings.errors.flagCommentFailed);
    }
  }, [flagCommentMutation, clearError, handleError, strings]);

  // Return the hook interface
  return {
    // Data
    comments,
    pinnedComments,
    commentCount,

    // State
    loading: commentsQuery.isLoading || pinnedCommentsQuery.isLoading,
    refreshing: commentsQuery.isRefetching || pinnedCommentsQuery.isRefetching,
    replyLoading,
    hiddenPinnedComments,

    // Actions
    createComment,
    updateComment,
    deleteComment,
    reactToComment,
    reportUser,

    // UI Actions
    hidePinnedComment,
    refreshComments,
    loadMoreComments,

    // Pagination
    hasMoreComments,
    isLoadingMore: commentsQuery.isFetchingNextPage,

    // Error handling
    error,
    clearError,

    // Additional actions
    togglePinComment,
    flagComment,
  };
};

export default useGroupDiscussion; 